import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { changeEventSchema, type ChangeEvent, type LiveTopic, topicOf } from '@forecastify/shared';
import { useSession } from '../session';
import { readSseBlocks, sseData, sseEvent } from './sse';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C21 · ONE stream per tab, above the router. R-19.                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **READ WITH `fetch`, NOT `EventSource`, AND THAT IS A DECISION RATHER THAN A PREFERENCE.**
 * `EventSource` is three lines instead of these and **cannot send headers**. Authenticating it
 * means the access token in the query string — every access log, every proxy log, browser history —
 * or in a cookie, and `decisions.md` R-11 put the access token in memory *only*, deliberately,
 * because a browser-held credential makes per-session revocation unenforceable. R-19 records this
 * so the next reader does not simplify it back.
 *
 * ⚠️ **One connection for the whole tab.** A stream per screen would be one long-lived request per
 * mounted route, each re-authenticated on every heartbeat, and each re-opened by every navigation.
 * This sits beside `PreferencesBoot`, above the router, so navigating never touches it.
 */

type Listener = (event: ChangeEvent | null) => void;

interface LiveContextValue {
  /** Subscribe to every frame. `null` means resync — refetch, do not try to reason about it. */
  readonly subscribe: (listener: Listener) => () => void;
  /** For the one place that should say so out loud: the notification bell's fallback. */
  readonly connected: boolean;
}

const LiveContext = createContext<LiveContextValue | null>(null);

/** How long to wait before reconnecting. Doubles to a ceiling, so a dead API is not hammered. */
const RETRY_MS = 2000;
const RETRY_CEILING_MS = 30_000;

export function LiveProvider({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const status = session.status;
  const workspaceId = session.workspaceId;

  /**
   * ⚠️⚠️ **THE SESSION GOES IN A REF AND IS NOT A DEPENDENCY.** `useSession()` builds a fresh
   * object every render, so listing it re-runs the effect on every render of the whole tree — and
   * for a long-lived connection that means tearing the stream down and reopening it, forever.
   * C20 shipped exactly this mistake in what is now `preferences-boot.tsx` and it produced ten concurrent `/account`
   * requests; here the symptom would be a product with no live updates and a very busy API.
   */
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const listeners = useRef(new Set<Listener>());
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (status !== 'signed-in' || workspaceId === null) return;

    const abort = new AbortController();
    let retry = RETRY_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const announce = (event: ChangeEvent | null): void => {
      /* A copy: a listener may unsubscribe itself while being called — a screen unmounting on the
       * frame that told it to refetch is ordinary, not an edge case. */
      for (const listener of [...listeners.current]) listener(event);
    };

    /**
     * One SSE frame.
     *
     * ⚠️ **Parsed with the shared schema, never cast.** `hard-won.md` C19-1 is four chunks of
     * invisible links caused by trusting a shape nothing checked; a live payload has the same
     * exposure and less to catch it, because nothing renders it directly.
     */
    const frame = (block: string): void => {
      const kind = sseEvent(block);
      if (kind === 'resync') {
        /* Not a replay — the server does not know what we missed. Refetch everything held. */
        announce(null);
        return;
      }
      if (kind === 'expired') {
        /* ⚠️ The session was revoked while the stream was open. Nothing to reconnect to; the
         * session layer's own refresh will discover it and send the person to sign-in. */
        abort.abort();
        return;
      }
      if (kind !== 'change') return;

      const parsed = changeEventSchema.safeParse(sseData(block));
      if (parsed.success) announce(parsed.data);
    };

    const read = async (): Promise<void> => {
      /**
       * ⚠️ **`session.request`, not a bare `fetch`.** It attaches the bearer token and the
       * workspace header, and — the part that matters for a connection meant to last hours — it
       * already does one refresh and one retry on a 401. Opening this with a token read directly
       * would mean a stream that dies at the first access-token expiry and reconnects into another
       * 401, forever.
       */
      const response = await sessionRef.current.request('/events/stream', {
        headers: { accept: 'text/event-stream' },
        signal: abort.signal,
      });
      if (!response.ok || response.body === null)
        throw new Error(`stream: ${String(response.status)}`);

      setConnected(true);
      retry = RETRY_MS;

      /**
       * ⚠️ **The framing lives in `sse.ts`, shared with C27's agent turn.** It used to be inline
       * here — buffer, split on a blank line, keep the tail — and the agent needed the identical
       * loop on a different path. Two copies of *"a chunk is not a frame"* is the duplicated
       * predicate `hard-won.md` warns about, and the drift would only show up on a slow connection.
       */
      for await (const block of readSseBlocks(response.body)) {
        frame(block);
      }
    };

    const loop = (): void => {
      void read()
        .catch(() => undefined)
        .finally(() => {
          if (abort.signal.aborted) return;
          setConnected(false);
          /* ⚠️ Backoff with a ceiling. A stream that ends immediately — a proxy refusing it, an API
           * restarting — would otherwise be a reconnect loop at full speed. */
          timer = setTimeout(loop, retry);
          retry = Math.min(retry * 2, RETRY_CEILING_MS);
        });
    };

    loop();

    return () => {
      abort.abort();
      if (timer !== null) clearTimeout(timer);
      setConnected(false);
    };
  }, [status, workspaceId]);

  const value = useMemo<LiveContextValue>(
    () => ({
      connected,
      subscribe: (listener) => {
        listeners.current.add(listener);
        return () => {
          listeners.current.delete(listener);
        };
      },
    }),
    [connected],
  );

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

/**
 * ⚠️ **Returns `null` outside a provider rather than throwing**, which is the opposite of
 * `useI18n`'s choice and deliberate. A missing translation provider means nothing can render; a
 * missing live provider means the screen is merely not live — and every screen test renders a
 * component without one. Throwing would make live updates a thing every test had to know about.
 */
export function useLive(): LiveContextValue | null {
  return useContext(LiveContext);
}

export { topicOf };
export type { LiveTopic };
